import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Search } from 'lucide-react';
import type { LeaveBalanceDto, LedgerEntryDto } from '@workforce/shared';
import { ApiError, api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useLeaveTypes } from '@/lib/leave';
import { useDepartments } from '@/lib/queries';
import { flag } from '@/lib/format';
import { t } from '@/lib/i18n';
import { BalanceCards } from '@/components/BalanceCards';
import { Avatar, Button, Card, EmptyState, ErrorBanner, Field, Input, Modal, PageHeader, Select, Spinner, Tabs, Textarea } from '@/components/ui';
import { LedgerTable } from './MyBalance';

interface Row {
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  countryCode: string;
  department: string | null;
  balances: LeaveBalanceDto[];
}

export function BalancesPage() {
  const thisYear = DateTime.now().year;
  const [year, setYear] = useState(thisYear);
  const [departmentId, setDepartmentId] = useState('');
  const [search, setSearch] = useState('');
  const types = useLeaveTypes();
  const deps = useDepartments();
  const deducting = (types.data ?? []).filter((lt) => lt.deductsBalance && lt.isActive);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  useEffect(() => {
    if (!leaveTypeId && deducting.length) setLeaveTypeId((deducting.find((lt) => lt.isPrimary) ?? deducting[0]!).id);
  }, [deducting, leaveTypeId]);
  const date = year === thisYear ? DateTime.now().toISODate()! : `${year}-06-30`;
  const q = useQuery({
    queryKey: ['leave', 'balances', { date, departmentId, search }],
    queryFn: () => api.get<Row[]>(`/leave/balances${qs({ date, departmentId, q: search })}`),
  });
  const [open, setOpen] = useState<Row | null>(null);

  return (
    <div>
      <PageHeader title={t('Vacation balances')} subtitle={t('Entitlement, usage and remaining days per employee. Click a row for the full history and adjustments.')} />
      <div className="card mb-4 grid grid-cols-1 gap-3 p-4 sm:grid-cols-4">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="ps-9" placeholder={t('Search employee')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)} aria-label={t('Leave type')}>
          {deducting.map((lt) => (
            <option key={lt.id} value={lt.id}>
              {t(lt.name)}
            </option>
          ))}
        </Select>
        <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} aria-label={t('Department')}>
          <option value="">{t('All departments')}</option>
          {deps.data?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
        <Select value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t('Leave year')}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
            <option key={y} value={y}>
              {t('Leave year {year}', { year: y })}
            </option>
          ))}
        </Select>
      </div>
      <ErrorBanner error={q.error} />
      <Card bodyClass="p-0">
        {q.isLoading ? (
          <Spinner />
        ) : !q.data?.length ? (
          <EmptyState title={t('No employees')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="th">{t('Employee')}</th>
                  <th className="th text-end">{t('Entitled')}</th>
                  <th className="th text-end">{t('Carried')}</th>
                  <th className="th text-end">{t('Adjusted')}</th>
                  <th className="th text-end">{t('Used')}</th>
                  <th className="th text-end">{t('Pending')}</th>
                  <th className="th text-end">{t('Remaining')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {q.data.map((r) => {
                  const b = r.balances.find((x) => x.leaveTypeId === leaveTypeId);
                  return (
                    <tr key={r.employeeId} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(r)}>
                      <td className="td">
                        <div className="flex items-center gap-3">
                          <Avatar name={r.employeeName} size="sm" />
                          <div>
                            <p className="font-medium text-slate-900">{r.employeeName}</p>
                            <p className="text-xs text-slate-500">
                              {flag(r.countryCode)} {r.department ?? '—'} · {r.employeeCode}
                            </p>
                          </div>
                        </div>
                      </td>
                      {b ? (
                        <>
                          <td className="td text-end tabular-nums">{b.entitled}</td>
                          <td className="td text-end tabular-nums">{b.carriedOver || '—'}</td>
                          <td className="td text-end tabular-nums">{b.adjusted ? (b.adjusted > 0 ? `+${b.adjusted}` : b.adjusted) : '—'}</td>
                          <td className="td text-end tabular-nums">{b.used}</td>
                          <td className="td text-end tabular-nums">{b.pending || '—'}</td>
                          <td className={`td text-end font-semibold tabular-nums ${b.remaining < 0 ? 'text-red-600' : 'text-slate-900'}`}>{b.remaining}</td>
                        </>
                      ) : (
                        <td className="td text-end text-xs text-slate-400" colSpan={6}>
                          {t('Not applicable')}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && <EmployeeBalanceModal row={open} date={date} initialTypeId={leaveTypeId} onClose={() => setOpen(null)} />}
    </div>
  );
}

export function EmployeeBalanceModal({ row, date, initialTypeId, onClose }: { row: Pick<Row, 'employeeId' | 'employeeName'>; date: string; initialTypeId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canAdjust = can('leave.balances.adjust');
  const [typeId, setTypeId] = useState(initialTypeId ?? '');
  const [mode, setMode] = useState<'history' | 'adjust' | 'allowance'>('history');
  const [delta, setDelta] = useState('');
  const [entitled, setEntitled] = useState('');
  const [reason, setReason] = useState('');
  const detail = useQuery({
    queryKey: ['leave', 'employee', row.employeeId, date],
    queryFn: () => api.get<{ balances: LeaveBalanceDto[] }>(`/leave/employees/${row.employeeId}?date=${date}`),
  });
  const balances = detail.data?.balances ?? [];
  const current = balances.find((b) => b.leaveTypeId === typeId) ?? balances.find((b) => b.isPrimary) ?? balances[0];
  useEffect(() => {
    if (current) setEntitled(String(current.entitled));
  }, [current?.leaveTypeId, current?.entitled]); // eslint-disable-line react-hooks/exhaustive-deps
  const ledger = useQuery({
    queryKey: ['leave', 'ledger', current?.balanceId],
    queryFn: () => api.get<LedgerEntryDto[]>(`/leave/balances/${current!.balanceId}/ledger`),
    enabled: !!current?.balanceId,
  });
  const save = useMutation({
    mutationFn: () =>
      mode === 'adjust'
        ? api.post('/leave/balances/adjust', { employeeId: row.employeeId, leaveTypeId: current!.leaveTypeId, date, delta: Number(delta), reason })
        : api.post('/leave/balances/entitlement', { employeeId: row.employeeId, leaveTypeId: current!.leaveTypeId, date, entitled: Number(entitled), reason }),
    onSuccess: () => {
      setMode('history');
      setDelta('');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['leave'] });
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  return (
    <Modal open onClose={onClose} title={t('{name} — leave balances', { name: row.employeeName })} size="lg">
      {detail.isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          <BalanceCards balances={balances} onSelect={(b) => setTypeId(b.leaveTypeId)} selectedId={current?.leaveTypeId} />
          {current && (
            <>
              <Tabs
                tabs={[
                  { value: 'history', label: t('{type} history', { type: t(current.leaveTypeName) }) },
                  ...(canAdjust && current.deductsBalance
                    ? [
                        { value: 'adjust' as const, label: t('Adjust balance') },
                        { value: 'allowance' as const, label: t('Change allowance') },
                      ]
                    : []),
                ]}
                value={mode}
                onChange={(v) => {
                  save.reset();
                  setMode(v);
                }}
              />
              {mode === 'history' &&
                (current.balanceId ? (
                  ledger.isLoading ? <Spinner /> : <LedgerTable entries={ledger.data ?? []} />
                ) : (
                  <EmptyState title={t('No movements yet in this leave year')} />
                ))}
              {mode !== 'history' && (
                <div className="max-w-md space-y-4">
                  <ErrorBanner error={save.error && !Object.keys(fe).length ? save.error : null} />
                  {mode === 'adjust' ? (
                    <Field label={t('Days to add (use a minus sign to deduct)')} required error={fe.delta} hint={t('Whole or half days, e.g. 2, -1, 0.5. Use this for opening balances or compensation days.')}>
                      <Input type="number" step="0.5" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder={t('e.g. 2 or -1')} />
                    </Field>
                  ) : (
                    <Field label={t('Yearly allowance for this employee (currently {n})', { n: current.entitled })} required error={fe.entitled} hint={t('Overrides the policy allowance for this leave year only.')}>
                      <Input type="number" step="0.5" min={0} value={entitled} onChange={(e) => setEntitled(e.target.value)} />
                    </Field>
                  )}
                  <Field label={t('Reason')} required error={fe.reason} hint={t('Recorded in the balance history and audit log, and shown to the employee.')}>
                    <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <Button
                    loading={save.isPending}
                    disabled={reason.trim().length < 3 || (mode === 'adjust' ? !delta || Number(delta) === 0 : entitled === '')}
                    onClick={() => save.mutate()}
                  >
                    {mode === 'adjust' ? t('Apply adjustment') : t('Save allowance')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
